import classNames from "classnames";
import React, { Children, HTMLAttributes, ReactElement, useEffect, useRef, useState } from "react";

import { Tab, TabContent, TabNavItem, TabNavs } from "./Tabs.styled";
import { resizeTabs } from "./utils";

export type TabsProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Set tab nav key
   */
  activeTabKey: string;

  /**
   * Set tab nav being equal with and full
   */
  justify?: boolean;

  /**
   * Set tab to be full height (to the bottom of page)
   */
  fullHeight?: boolean;

  /**
   * Tab nav clicked callback function
   */
  onChange: (key: string) => void;
};

const defaultProps = {
  activeTabKey: "",
  justify: false,
  fullHeight: false,
  onChange: () => {}
};

const TabsComp = React.forwardRef<HTMLDivElement, TabsProps>((props, ref) => {
  const { children, className, activeTabKey, justify, fullHeight, onChange } = props;
  const tabClass = classNames("c-tabs", className);
  const [activeTab, setActiveTab] = useState(activeTabKey);

  const tabContentRef = useRef(null);

  function onClickTabItem(tabKey: string) {
    if (activeTab === tabKey) return;
    setActiveTab(tabKey);
    onChange(tabKey);
  }

  function renderNavs() {
    return Children.map(children, (child: ReactElement) => {
      const { label, tabKey, "data-qaid": dataQaid } = child.props;
      const tabNavClass = classNames("c-tabs__nav-item", { "is-active": activeTab === tabKey });

      return (
        <TabNavItem
          key={tabKey}
          className={tabNavClass}
          justify={justify}
          onClick={() => onClickTabItem(tabKey)}
          data-testid="qa-tab-nav-item"
          data-qaid={`${dataQaid}-tab-navs`}
        >
          {label}
        </TabNavItem>
      );
    });
  }

  useEffect(() => {
    setActiveTab(activeTabKey);
  }, [activeTabKey]);

  useEffect(() => {
    if (fullHeight) resizeTabs(tabContentRef);
  }, []);

  return (
    <div ref={ref} className={tabClass} data-testid="qa-tab">
      <TabNavs className="c-tabs__navs" data-testid="qa-tab-navs">
        {renderNavs()}
      </TabNavs>
      <TabContent ref={tabContentRef} className="c-tabs__content" justify={justify} data-testid="qa-tab-content">
        {Children.map(children, (child: ReactElement) => {
          if (child.props.tabKey !== activeTab) return undefined;
          return child.props.children;
        })}
      </TabContent>
    </div>
  );
});

const Tabs = Object.assign(TabsComp, {
  Tab,
  defaultProps
});

export default Tabs;
